import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { wholesaleService } from '../../services/wholesaleService';
// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

// Form component
const WholesaleRegisterForm = () => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    abcLicenseNumber: '',
    resaleNumber: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    termsAccepted: false
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (files && files[0]) {
      const fileType = files[0].type;
      const validTypes = ['application/pdf', 'image/jpeg', 'image/png'];
      
      if (!validTypes.includes(fileType)) {
        setError('Please upload a PDF or image file');
        e.target.value = '';
        return;
      }

      setFormData(prev => ({
        ...prev,
        [name]: files[0]
      }));
    }
  };

  const validateStep = (currentStep) => {
    setError('');
    switch (currentStep) {
      case 1:
        if (!formData.companyName || !formData.firstName || !formData.lastName || 
            !formData.email || !formData.phone) {
          setError('Please fill in all required fields');
          return false;
        }
        if (formData.password !== formData.confirmPassword) {
          setError('Passwords do not match');
          return false;
        }
        break;
      case 2:
        if (!formData.abcLicenseNumber || !formData.resaleNumber) {
          setError('License numbers are required');
          return false;
        }
        break;
      case 3:
        if (!formData.addressLine1 || !formData.city || !formData.state || !formData.zipCode) {
          setError('Please provide complete address information');
          return false;
        }
        break;
      case 4:
        if (!formData.termsAccepted) {
          setError('You must accept the terms to continue');
          return false;
        }
        break;
      default:
        return true;
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(prev => prev + 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    try {
      // Create payment method
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement),
      });

      if (stripeError) throw new Error(stripeError.message);

      // Create form data for file upload
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (key !== 'abcLicenseFile' && key !== 'resaleCertificateFile') {
          formDataToSend.append(key, formData[key]);
        }
      });
      formDataToSend.append('paymentMethodId', paymentMethod.id);
      
      if (formData.abcLicenseFile) {
        formDataToSend.append('abcLicenseFile', formData.abcLicenseFile);
      }
      if (formData.resaleCertificateFile) {
        formDataToSend.append('resaleCertificateFile', formData.resaleCertificateFile);
      }

      // Register customer
      const response = await wholesaleService.register(formDataToSend);

      if (response.data?.success || response.data?.data?.success) {
        navigate('/wholesale/registration-success');
      }
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container my-5">
      <div className="row justify-content-center">
        <div className="col-md-8">
          <div className="card shadow">
            <div className="card-body">
              <h2 className="text-center mb-4">Wholesale Account Registration</h2>
              
              <div className="mb-4">
                <div className="progress">
                  <div 
                    className="progress-bar" 
                    style={{ width: `${(step / 4) * 100}%` }}
                  ></div>
                </div>
              </div>

              {error && (
                <div className="alert alert-danger">{error}</div>
              )}

              <form onSubmit={handleSubmit}>
                {/* Step 1: Basic Information */}
                {step === 1 && (
                  <div className="basic-info">
                    <h4>Company Information</h4>
                    <div className="mb-3">
                      <input
                        type="text"
                        className="form-control"
                        name="companyName"
                        placeholder="Company Name"
                        value={formData.companyName}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="row mb-3">
                      <div className="col">
                        <input
                          type="text"
                          className="form-control"
                          name="firstName"
                          placeholder="First Name"
                          value={formData.firstName}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <div className="col">
                        <input
                          type="text"
                          className="form-control"
                          name="lastName"
                          placeholder="Last Name"
                          value={formData.lastName}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="mb-3">
                      <input
                        type="email"
                        className="form-control"
                        name="email"
                        placeholder="Email Address"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <input
                        type="tel"
                        className="form-control"
                        name="phone"
                        placeholder="Phone Number"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <input
                        type="password"
                        className="form-control"
                        name="password"
                        placeholder="Password"
                        value={formData.password}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <input
                        type="password"
                        className="form-control"
                        name="confirmPassword"
                        placeholder="Confirm Password"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                )}

                {/* Step 2: License Information */}
                {step === 2 && (
                  <div className="license-info">
                    <h4>License Information</h4>
                    <div className="mb-3">
                      <input
                        type="text"
                        className="form-control"
                        name="abcLicenseNumber"
                        placeholder="ABC License Number"
                        value={formData.abcLicenseNumber}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <input
                        type="text"
                        className="form-control"
                        name="resaleNumber"
                        placeholder="Resale Number"
                        value={formData.resaleNumber}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">ABC License (PDF or Image)</label>
                      <input
                        type="file"
                        className="form-control"
                        name="abcLicenseFile"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={handleFileChange}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Resale Certificate (PDF or Image)</label>
                      <input
                        type="file"
                        className="form-control"
                        name="resaleCertificateFile"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={handleFileChange}
                        required
                      />
                    </div>
                  </div>
                )}

                {/* Step 3: Address Information */}
                {step === 3 && (
                  <div className="address-info">
                    <h4>Business Address</h4>
                    <div className="mb-3">
                      <input
                        type="text"
                        className="form-control"
                        name="addressLine1"
                        placeholder="Street Address"
                        value={formData.addressLine1}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <input
                        type="text"
                        className="form-control"
                        name="addressLine2"
                        placeholder="Suite, Unit, etc. (optional)"
                        value={formData.addressLine2}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="row mb-3">
                      <div className="col">
                        <input
                          type="text"
                          className="form-control"
                          name="city"
                          placeholder="City"
                          value={formData.city}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <div className="col">
                        <input
                          type="text"
                          className="form-control"
                          name="state"
                          placeholder="State"
                          value={formData.state}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <div className="col">
                        <input
                          type="text"
                          className="form-control"
                          name="zipCode"
                          placeholder="ZIP Code"
                          value={formData.zipCode}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 4: Payment Information and Terms */}
                {step === 4 && (
                  <div className="payment-terms">
                    <h4>Payment Information</h4>
                    <div className="mb-4">
                      <CardElement
                        options={{
                          style: {
                            base: {
                              fontSize: '16px',
                              color: '#424770',
                              '::placeholder': {
                                color: '#aab7c4',
                              },
                            },
                            invalid: {
                              color: '#9e2146',
                            },
                          },
                        }}
                      />
                    </div>

                    <div className="mb-4">
                      <h5>Payment Terms</h5>
                      <div className="terms-container p-3 bg-light rounded">
                        <p>In accordance with Section 25509 of the California Business and Professions Code:</p>
                        <ul>
                          <li>Payment will be processed 30 days from the date of delivery</li>
                          <li>Your card will be pre-authorized at the time of order</li>
                          <li>Full payment terms and conditions apply</li>
                        </ul>
                      </div>
                    </div>

                    <div className="form-check mb-3">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        name="termsAccepted"
                        id="termsAccepted"
                        checked={formData.termsAccepted}
                        onChange={handleChange}
                        required
                      />
                      <label className="form-check-label" htmlFor="termsAccepted">
                        I accept the terms and conditions
                      </label>
                    </div>
                  </div>
                )}

                {/* Navigation Buttons */}
                <div className="d-flex justify-content-between mt-4">
                  {step > 1 && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setStep(prev => prev - 1)}
                    >
                      Back
                    </button>
                  )}
                  {step < 4 ? (
                    <button
                      type="button"
                      className="btn btn-primary ms-auto"
                      onClick={handleNext}
                    >
                      Next
                    </button>
                  ) : (
                    <button
                      type="submit"
                      className="btn btn-primary ms-auto"
                      disabled={loading || !stripe}
                    >
                      {loading ? 'Processing...' : 'Submit Registration'}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Wrap the form with Stripe Elements
const WholesaleRegister = () => (
  <Elements stripe={stripePromise}>
    <WholesaleRegisterForm />
  </Elements>
);

export default WholesaleRegister;